import PublicLayout from "@/components/PublicLayout";
import Modal from "@/components/Modal";
import { useEffect, useState } from "react";
import api, { formatError } from "@/lib/api";
import { Plus, CheckCircle2, Circle, Trash2, HeartHandshake } from "lucide-react";
import { toast } from "sonner";

export default function PrayerJournal() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ title: "", content: "", answered: false });

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/prayers");
      setItems(data.items);
    } catch (_e) { /* ignore */ }
    setLoading(false);
  };

  useEffect(() => {
    load();
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post("/prayers", form);
      toast.success("Your prayer is recorded.");
      setOpen(false);
      setForm({ title: "", content: "", answered: false });
      load();
    } catch (e) {
      toast.error(formatError(e.response?.data?.detail) || e.message);
    }
  };

  const toggle = async (id) => {
    try {
      await api.patch(`/prayers/${id}`);
      load();
    } catch (_e) {
      toast.error("Could not update.");
    }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/prayers/${id}`);
      setItems(items.filter((x) => x.id !== id));
    } catch (_e) {
      toast.error("Could not delete.");
    }
  };

  return (
    <PublicLayout>
      <section className="bg-brand-base">
        <div className="max-w-5xl mx-auto px-5 lg:px-8 py-10">
          <div className="flex items-end justify-between flex-wrap gap-4">
            <div>
              <p className="text-xs uppercase tracking-widest text-brand-quiet">Prayer Journal</p>
              <h1 className="mt-2 font-serif text-4xl sm:text-5xl text-brand-ink tracking-tight">
                Speak. He listens.
              </h1>
              <p className="mt-2 text-brand-quiet max-w-xl">
                Write your prayers. Mark them answered. Watch the faithfulness of God unfold.
              </p>
            </div>
            <button
              onClick={() => setOpen(true)}
              data-testid="prayer-add-button"
              className="rounded-full bg-brand-sky text-white hover:bg-brand-skyDark transition-colors px-5 py-3 font-medium inline-flex items-center gap-2"
            >
              <Plus className="w-4 h-4" strokeWidth={1.8} /> New prayer
            </button>
          </div>

          <div className="mt-8 grid sm:grid-cols-2 gap-4">
            {loading ? (
              <p className="text-brand-quiet">Loading…</p>
            ) : items.length === 0 ? (
              <div
                className="sm:col-span-2 rounded-2xl bg-white border border-brand-line p-10 text-center"
                data-testid="prayer-empty-state"
              >
                <HeartHandshake className="w-8 h-8 text-brand-skyDark mx-auto mb-3" strokeWidth={1.4} />
                <p className="scripture-quote text-2xl text-brand-ink">
                  “Be anxious for nothing, but in everything by prayer…”
                </p>
                <p className="text-xs text-brand-muted mt-1">— Philippians 4:6</p>
              </div>
            ) : (
              items.map((p) => (
                <article
                  key={p.id}
                  data-testid={`prayer-item-${p.id}`}
                  className={`rounded-2xl border p-6 transition-all ${
                    p.answered
                      ? "bg-[#f3f7f4] border-[#cbded2]"
                      : "bg-white border-brand-line"
                  }`}
                >
                  <div className="flex items-start gap-3">
                    <button
                      onClick={() => toggle(p.id)}
                      data-testid={`prayer-toggle-${p.id}`}
                      className="mt-1 text-brand-sage"
                      aria-label="Toggle answered"
                    >
                      {p.answered ? (
                        <CheckCircle2 className="w-5 h-5" strokeWidth={1.6} />
                      ) : (
                        <Circle className="w-5 h-5 text-brand-line" strokeWidth={1.6} />
                      )}
                    </button>
                    <div className="flex-1 min-w-0">
                      <h3 className="font-serif text-2xl text-brand-ink leading-snug">
                        {p.title}
                      </h3>
                      <p className="text-xs text-brand-muted mt-0.5">
                        {new Date(p.created_at).toLocaleDateString(undefined, {
                          year: "numeric",
                          month: "long",
                          day: "numeric",
                        })}
                        {p.answered && " · Answered"}
                      </p>
                      <p className="mt-3 text-sm text-brand-quiet leading-relaxed whitespace-pre-wrap">
                        {p.content}
                      </p>
                    </div>
                    <button
                      onClick={() => remove(p.id)}
                      data-testid={`prayer-delete-${p.id}`}
                      className="text-brand-muted hover:text-brand-setback p-1.5 rounded-full hover:bg-brand-mist"
                      aria-label="Delete"
                    >
                      <Trash2 className="w-4 h-4" strokeWidth={1.5} />
                    </button>
                  </div>
                </article>
              ))
            )}
          </div>
        </div>
      </section>

      <Modal open={open} onClose={() => setOpen(false)} testid="prayer-modal">
        <form
          onSubmit={submit}
          className="rounded-2xl bg-white border border-brand-line p-7 space-y-4"
        >
          <h2 className="font-serif text-3xl text-brand-ink">A new prayer</h2>
          <div>
            <label className="block text-xs uppercase tracking-wider text-brand-quiet mb-1.5">Title</label>
            <input
              required
              value={form.title}
              onChange={(e) => setForm({ ...form, title: e.target.value })}
              data-testid="prayer-title-input"
              placeholder="For peace in my family…"
              className="w-full rounded-xl bg-brand-base border border-brand-line focus:ring-2 focus:ring-brand-sky/40 focus:border-brand-sky outline-none px-3 py-2.5 text-brand-ink"
            />
          </div>
          <div>
            <label className="block text-xs uppercase tracking-wider text-brand-quiet mb-1.5">Your prayer</label>
            <textarea
              required
              rows={5}
              value={form.content}
              onChange={(e) => setForm({ ...form, content: e.target.value })}
              data-testid="prayer-content-input"
              className="w-full rounded-xl bg-brand-base border border-brand-line focus:ring-2 focus:ring-brand-sky/40 focus:border-brand-sky outline-none px-3 py-2.5 text-brand-ink"
            />
          </div>
          <div className="flex justify-end gap-2">
            <button
              type="button"
              onClick={() => setOpen(false)}
              data-testid="prayer-cancel-button"
              className="rounded-full px-5 py-2.5 text-brand-quiet hover:bg-brand-mist"
            >
              Cancel
            </button>
            <button
              type="submit"
              data-testid="prayer-submit-button"
              className="rounded-full bg-brand-sky text-white hover:bg-brand-skyDark px-5 py-2.5 font-medium"
            >
              Save prayer
            </button>
          </div>
        </form>
      </Modal>
    </PublicLayout>
  );
}
